import React, {
  useState,
  useRef,
  useEffect,
  cloneElement,
  Fragment,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import get from 'lodash/get';
import PropTypes from 'prop-types';

import Icon from '../../../../../Icon';
import useMenuPosition from './hooks/useMenuPosition';

import styles from './index.styl';

const MENU_BOTTOM_MARGIN = 30;

const MultipleOutcomesMenu = ({
  children,
  isConsentHasMultipleOutcomes,
  titles,
}) => {
  const [openMenu, setOpenMenu] = useState(false);

  const menuRef = useRef(null);

  const { getMenuPosition, buttonRef, position } = useMenuPosition(
    isConsentHasMultipleOutcomes,
  );

  const className = get(children.props, 'className', '');

  const handleOutcomeTitleClick = e => {
    const button = e.target.closest('button');

    if (button) {
      const title = button.textContent;
      children.props.onClick({ outcomeTitle: title });
      setOpenMenu(false);
    }
  };

  const onAcceptButtonClick = () => {
    if (isConsentHasMultipleOutcomes) {
      getMenuPosition();

      setOpenMenu(prev => !prev);
    } else {
      children.props.onClick();
    }
  };

  const enhancedChildren = cloneElement(children, {
    ...children.props,
    ref: buttonRef,
    onClick: onAcceptButtonClick,
    className: cn(className, { [styles.disableButton]: openMenu }),
  });

  const menuPositionStyle = useMemo(
    () => ({
      top: position.top + MENU_BOTTOM_MARGIN,
      left: position.left + position.width,
    }),
    [position],
  );

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const handleClickOutside = event => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenu(false);
      }
    };

    document.addEventListener('pointerdown', handleClickOutside, { signal });
    document.addEventListener('mousedown', handleClickOutside, { signal });
    document.addEventListener('touchstart', handleClickOutside, { signal });
    document.addEventListener('keydown', handleClickOutside, { signal });

    return () => {
      controller.abort();
    };
  }, []);

  return (
    <Fragment>
      {createPortal(
        <div className={styles.wrapper} style={menuPositionStyle}>
          {openMenu && (
            <ul
              className={styles.menuList}
              role="menu"
              ref={menuRef}
              onClick={handleOutcomeTitleClick}
            >
              {titles.map(item => {
                return (
                  <li key={item}>
                    <button
                      type="button"
                      className={styles.listItem}
                      style={{ minWidth: position.width }}
                    >
                      <div className={styles.title}>{item}</div>
                      <Icon
                        name="arrow-right"
                        size={16}
                        className={styles.icon}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>,
        document.body,
      )}
      {enhancedChildren}
    </Fragment>
  );
};

MultipleOutcomesMenu.propTypes = {
  children: PropTypes.node,
  isConsentHasMultipleOutcomes: PropTypes.bool,
  titles: PropTypes.arrayOf(PropTypes.string),
};

export default MultipleOutcomesMenu;



// WEBPACK FOOTER //
// ./src/components/Healthboard/Components/VisitConsents/ConsentDetailsPopup/MultipleOutcomesMenu/index.jsx